import type { ArmourySlotView, TreasureView } from "@highmarch/app";
import { ArmouryEntry, BalanceData, OfferItem, RARITY_IDS, Rarity, SkinData } from "@highmarch/domain";
import { SKIN_LOOKS } from "../../render/skins/looks";
import { RARITY_LABEL } from "../gearUi";
import { essenceWords, sigilWords } from "../forgeArt";
import { supplyName } from "../supplies/supplyArt";

/**
 * The Trophy Hall page's view models (MP-23, docs/SHOP.md §9.7): pure functions from the app's `TrophyHallView` rows
 * to what the page draws — each Treasure's name, kind and art path, each Armoury entry's name, picture and rarity,
 * the odds item a chest shows, and the toasts for a refusal. No DOM, no rules: what is on the shelf, its price, its
 * cap and whether it can be bought are @highmarch/app `TrophyHall`'s answers.
 */

/** The page's two tabs. */
export type TrophyTab = "treasures" | "armoury";
export const TROPHY_TABS: readonly { readonly key: TrophyTab; readonly label: string }[] = [{ key: "treasures", label: "Treasures" }, { key: "armoury", label: "Armoury" }];

/** What a cosmetic kind is called on a Treasure ("Champion's Laurel banner"). */
const KIND_WORD: Readonly<Record<string, string>> = { emblem: "banner", flag: "banner", road: "road", frame: "title frame", kill: "kill effect", tower: "tower skin", fortress: "fortress", troop: "troop skin" };

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** A Treasure's display name: its look's name (render/skins/looks.ts), else the id's last part, with its kind. */
export function treasureName(skin: SkinData): string {
  const look = SKIN_LOOKS[skin.id];
  const kind = KIND_WORD[skin.kind] ?? skin.kind;
  const base = look?.name ?? cap((skin.id.split(".").pop() ?? skin.id).replace(/_/g, " "));
  const last = kind.split(" ").pop() ?? kind;   // "Gold Frame" is a title frame already, not "Gold Frame title frame"
  return base.toLowerCase().endsWith(kind) || base.toLowerCase().endsWith(last) ? base : `${base} ${kind}`;
}

/** A Treasure's line in the confirm: its look's blurb, else what the kind does. */
export function treasureBlurb(skin: SkinData): string {
  const look = SKIN_LOOKS[skin.id];
  if (look) return look.blurb;
  switch (skin.kind as string) {
    case "frame": return "An ornate frame round your Commander's name and title";
    case "kill": return "How raiders fall: a battle effect, looks only";
    case "road": return "A new road surface under every march";
    default: return "A banner for your fortress";
  }
}

/** A Treasure's picture under art/interface/ (the cosmetic's own swatch: skins/<kind>_<name>.png). */
export function treasureArtPath(skin: SkinData): string { return `skins/${skin.id.replace(".", "_")}.png`; }

/** A Treasure's state on its tile. */
export function treasureState(t: TreasureView): "owned" | "short" | "ok" { return t.owned ? "owned" : t.blocked === "not enough" ? "short" : "ok"; }

/** What an Armoury entry is, for its tile and confirm. */
export interface EntryLook {
  readonly name: string;
  /** The tile's count corner ("60", "×3"); "" for none. */
  readonly count: string;
  /** The rarity jewel its well wears (chests and sigils); null for the navy well. */
  readonly rarity: Rarity | null;
  /** A chest: what its odds sheet shows (null for anything not rolled). */
  readonly odds: OfferItem | null;
  /** The confirm's line: where it goes. */
  readonly detail: string;
}

export function entryLook(B: BalanceData, s: Pick<ArmourySlotView, "entry" | "supplies">): EntryLook {
  const e: ArmouryEntry = s.entry, r = e.reward;
  if (r === null) {
    const names = s.supplies.map((id) => supplyName(B, id));
    return { name: `${e.supplies} supplies`, count: e.supplies > 1 ? `×${e.supplies}` : "", rarity: null, odds: null, detail: `This week's pick: ${names.join(", ")}. For your next battles.` };
  }
  if (r.chest !== null) {
    return { name: `${RARITY_LABEL[r.chest]} chest`, count: "", rarity: r.chest, odds: { kind: "chest", rarity: r.chest, count: 1 }, detail: "It opens at once: gear, gold and a speed-up at your level." };
  }
  if (r.sigil !== null) {
    return { name: cap(sigilWords(r.sigil, Math.max(1, r.sigils)).replace(/^1 /, "")), count: r.sigils > 1 ? `×${r.sigils}` : "", rarity: r.sigil, odds: null, detail: "Stands in for one item in a fusion of its rarity." };
  }
  return { name: "Forge Essence", count: String(r.essence), rarity: null, odds: null, detail: `${cap(essenceWords(r.essence))} for raising gear in the Forge.` };
}

/** The rarity id a well's class names (`r-epic`), for tests and hooks. */
export function rarityId(r: Rarity | null): string { return r === null ? "" : RARITY_IDS[r]; }

/** Why a tap did nothing, as a toast. */
export function trophyRefusal(code: string, minLevel = 0): string {
  switch (code) {
    case "not enough": case "not enough Trophies": return "Not enough Trophies: win them in Boss Week, Race Week, the Odyssey, weekly tracks, the Trial, Endless and Iron";
    case "sold out": return "Sold out this week. The Armoury restocks on Monday";
    case "locked": return minLevel > 0 ? `Clear level ${minLevel} to buy this` : "Not open yet";
    case "owned": return "Already yours: wear it from Skins";
    case "you hold all you can carry": return "You hold all the supplies you can carry. Use some first";
    case "offer changed": case "not this week": return "The stock just changed: take another look";
    case "not saving here": return "The game is open in another tab";
    case "not here": return "The Trophy Hall opens in the hub";
    default: return "Not right now";
  }
}
